//@author: devin

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnInit,
    output,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25ItemI } from "../../pojo/S25ItemI";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { jSith } from "../../util/jquery-replacement";
import { InstanceCount } from "../../util/s25.instance.count";
import { S25EditableModule } from "../s25-editable/s25.editable.module";
import { S25IconComponent } from "../s25-icon/s25.icon.component";

@TypeManagerDecorator("s25-ng-multiselect-item")
@Component({
    selector: "s25-ng-multiselect-item",
    template: `
        @if (type() === "none") {
            <div class="s25-multiselect-item-none">
                <div class="s25-multiselect-item-col s25-multiselect-item-mask"><i>&#160;</i></div>
                <div class="s25-multiselect-item-col s25-multiselect-item-text">None</div>
            </div>
        }

        @if (type() !== "none") {
            <div
                class="s25-multiselect-item"
                [class.no-pointer]="readonly()"
                [id]="id"
                (click)="onClick($event)"
                (keydown.enter)="onKeyboardSelect($event)"
                (keydown.space)="onKeyboardSelect($event)"
                [tabIndex]="0"
                [attr.aria-label]="getAriaLabel()"
            >
                <div class="tb-body">
                    @if (type() === "check") {
                        <div class="tb-col s25-checkmark-col">
                            <input
                                type="checkbox"
                                [disabled]="readonly()"
                                [(ngModel)]="item().checked"
                                class="c-label--input"
                                name="multiselect-check"
                                [tabIndex]="-1"
                            />
                            <label class="c-label--checkbox c-margin-bottom--none" for="multiselect-check"></label>
                        </div>
                    }
                    @if (type() === "radio") {
                        <div class="tb-col s25-checkmark-col">
                            <input type="radio" [disabled]="readonly()" [checked]="item().checked" [tabIndex]="-1" />
                        </div>
                    }
                    @if (type() === "cross" && !readonly()) {
                        <div class="cross">
                            <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                        </div>
                    }
                    <div class="s25-multiselect-item-text-col {{ type() === 'cross' ? 'ngInlineBlock' : 'tb-col' }}">
                        <span>{{ item().itemName }}</span>
                        @if (item().isInactive === 1) {
                            <span class="not_active"> [Not Active] </span>
                        }
                        @if (item().itemDesc && type() !== "cross") {
                            <span class="not_active">{{ item().itemDesc }}</span>
                        }
                        @if (showInactive()) {
                            <span class="not_active">Inactive</span>
                        }
                    </div>
                    @if (hasQuantity() && type() !== "cross") {
                        <div>
                            <div class="tb-body">
                                <s25-ng-editable-number
                                    class="picker-col tb-col"
                                    type="integer"
                                    [onUpdate]="onQuantityUpdate"
                                    [readOnly]="item().quantityMax <= 1"
                                    [min]="1"
                                    [max]="item().quantityMax"
                                    [(val)]="item().quantity"
                                    (click)="stopClick($event)"
                                >
                                </s25-ng-editable-number>
                                @if (item().quantityMax) {
                                    <div class="total-col tb-col">
                                        /<span class="total-quantity">{{ item().quantityMax }}</span>
                                    </div>
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>
        }
    `,
    styles: `
        .s25-multiselect-item {
            padding: 0;
        }

        .s25-multiselect-item .tb-body {
            display: flex;
            gap: 0.5em;
            padding: 0.25em 0.5em 0.25em 0.25em;
        }

        .s25-multiselect-item .cross {
            margin: auto;
        }

        .s25-multiselect-item .cross svg {
            margin: 0 !important;
        }

        .s25-multiselect-item .s25-multiselect-item-text-col {
            display: flex;
            flex-wrap: wrap;
            column-gap: 0.5em;
            align-items: center;
            overflow: hidden;
        }

        ::ng-deep .nm-party--on s25-ng-multiselect-item .s25-multiselect-item svg {
            color: rgba(255, 255, 255, 0.5) !important;
        }

        ::ng-deep .nm-party--on s25-ng-multiselect-item .s25-multiselect-item:hover svg {
            color: #e8e9e8 !important;
        }

        ::ng-deep .nm-party--on s25-ng-multiselect-item .s25-multiselect-item-text-col {
            color: #fff;
        }

        ::ng-deep .nm-party--on s25-ng-multiselect-item .s25-multiselect-item-text-col .not_active {
            color: #c7c7c7;
        }

        .no-pointer {
            pointer-events: none !important;
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, S25IconComponent, S25EditableModule],
})
export class S25MultiselectItemComponent extends InstanceCount implements OnInit {
    readonly type = input.required<"check" | "radio" | "cross" | "none">();
    readonly showInactive = input(false);
    readonly autoFocus = input(false);
    readonly item = input<S25ItemI>();
    readonly action = input<($event: any) => any>();
    readonly disabled = input(false);
    readonly hasQuantityInput = input(false, { alias: "hasQuantity" });

    static QuantityTypes = new Set([607]);
    readonly hasQuantity = computed(() => {
        const item = this.item();
        if (item?.hasQuantity) return true;
        if (this.hasQuantityInput()) return true;
        if (item?.itemTypeId && S25MultiselectItemComponent.QuantityTypes.has(item.itemTypeId)) return true;
        return false;
    });

    readonly readonly = computed(() => this.disabled() || !!this.item()?.isPermanent);
    readonly clear = output<void>();
    readonly select = output<void>();
    readonly remove = output<void>();
    readonly changed = output<void>();

    protected readonly id = `selectedItem-${this.instanceId}`;

    readonly elementRef = inject(ElementRef);
    readonly cd = inject(ChangeDetectorRef);

    onQuantityUpdate = () => {
        if (!this.item()!.checked) {
            this.onClick();
        }
    };

    refresh = () => {
        try {
            this.cd.detectChanges();
        } catch (error: any) {}
    };

    onKeyboardSelect = (event: Event) => {
        event.preventDefault(); // ANG-7069 prevent the default scroll behavior
        this.onClick(event);
    };

    onClick = ($event?: any) => {
        //if readonly, then its initial checked value cannot change!
        if (!this.readonly()) {
            if (this.type() === "radio") {
                this.clear.emit(); //select none first
                this.select.emit(); //then add new single selected item
            } else {
                //if cross has no checked status, set it to true so that toggle below removes it (bc toggle inverts checked status)
                if (this.type() === "cross" && S25Util.isUndefined(this.item()!.checked)) {
                    this.item()!.checked = true;
                }
                this.toggle();
            }
            this.changed.emit();
        }

        //any outside action()
        this.action()?.($event);

        this.cd.detectChanges();
    };

    stopClick = ($event?: any) => {
        $event.stopPropagation();
    };

    toggle = () => {
        this.item()!.checked = !this.item()!.checked;
        if (this.item()!.checked) {
            this.select.emit();
        } else {
            this.remove.emit();
        }
        this.cd.detectChanges();
    };

    getAriaLabel = () => {
        switch (this.type()) {
            case "check":
                return `${this.item()!.checked ? "uncheck" : "check"} ${this.item()!.itemName}`;
            case "radio":
                return `select ${this.item()!.itemName}`;
            case "cross":
                return `remove ${this.item()!.itemName}`;
            default:
                return "";
        }
    };

    ngOnInit() {
        if (this.autoFocus()) {
            const elem = jSith.focusable(this.elementRef.nativeElement, 0, true, true);
            elem?.focus();
        }
    }
}
